// src/components/HoursRow.jsx
// Jedan red = jedna osoba, 7 pod-redaka (PON-NED) s Glovo/Wolt/Ukupno satima po danu

import React, { useState, useMemo } from 'react';
import { db } from '../config/firebase';
import { collection, addDoc, updateDoc, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import Modal from 'react-modal';
import { FaEdit } from 'react-icons/fa';
import ExcelUploader from './ExcelUploader';
import PersonEditModal from './PersonEditModal';
import {
  partsFromDecimalHours,
  formatDecimalHoursDisplay,
  formatDecimalHoursSigned,
} from '../utils/durationUtils';
import {
  parseGodisnjiHours,
  parseGlovoHours,
  parseWoltHours,
} from '../utils/metricHours';
import '../css/HoursRow.css';

const toDecimalHours = (hours, minutes) => {
  const h = parseInt(hours, 10) || 0;
  const m = Math.min(59, Math.max(0, parseInt(minutes, 10) || 0));
  return h + m / 60;
};

/** Ime u max 2 retka: redak 1 = ime, redak 2 = prezime. Ako stane u 1, jedan redak. */
function formatNameForDisplay(name) {
  if (!name || typeof name !== 'string') return name || '';
  const trimmed = name.trim();
  if (!trimmed) return '';
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return trimmed;
  const prezime = parts.pop();
  const ime = parts.join(' ');
  return (
    <>
      <span className="hours-name-line1">{ime}</span>
      <span className="hours-name-line2">{prezime}</span>
    </>
  );
}

function HoursRow({
  person,
  dailyMetricsData = [],
  holidaysByDate = {},
  weeks = [],
  refreshData,
  hiddenColumns = {},
  onExcelUpload,
  selectedFirm = '',
  firms = [],
  onUpdatePerson,
  onArchivePerson,
}) {
  const [workModalOpen, setWorkModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [editPersonModalOpen, setEditPersonModalOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(null);
  const [currentPlatform, setCurrentPlatform] = useState(null);
  const [workDetails, setWorkDetails] = useState({
    glovoHours: 0,
    glovoMinutes: 0,
    woltHours: 0,
    woltMinutes: 0,
    godisnjiHours: 0,
    godisnjiMinutes: 0,
  });

  const metricsByDate = useMemo(() => {
    const map = new Map();
    dailyMetricsData.forEach((d) => {
      map.set(`${d.year}-${d.month}-${d.day}`, d);
    });
    return map;
  }, [dailyMetricsData]);

  const getMetricForDate = (year, month, day) => {
    const m = metricsByDate.get(`${year}-${month}-${day}`);
    if (!m) return null;
    const godisnjiH = parseGodisnjiHours(m);
    if (godisnjiH > 0) {
      return {
        isGodisnji: true,
        godisnjiHours: godisnjiH,
        glovoHours: 0,
        woltHours: 0,
        totalHours: godisnjiH,
      };
    }
    const glovoHours = parseGlovoHours(m);
    const woltHours = parseWoltHours(m);
    return {
      isGodisnji: false,
      godisnjiHours: 0,
      glovoHours,
      woltHours,
      totalHours: glovoHours + woltHours,
    };
  };

  const openWorkModal = (dateInfo, platform) => {
    const { year, month, day } = dateInfo;
    const metric = metricsByDate.get(`${year}-${month}-${day}`);
    setCurrentDate({ year, month, day, ...dateInfo });
    setCurrentPlatform(platform);
    const glovo = partsFromDecimalHours(metric ? parseGlovoHours(metric) : 0);
    const wolt = partsFromDecimalHours(metric ? parseWoltHours(metric) : 0);
    const godisnji = partsFromDecimalHours(metric ? parseGodisnjiHours(metric) : 0);
    setWorkDetails({
      glovoHours: glovo.hours,
      glovoMinutes: glovo.minutes,
      woltHours: wolt.hours,
      woltMinutes: wolt.minutes,
      godisnjiHours: godisnji.hours,
      godisnjiMinutes: godisnji.minutes,
    });
    setWorkModalOpen(true);
  };

  const closeWorkModal = () => {
    setWorkModalOpen(false);
    setDeleteConfirmOpen(false);
    setCurrentDate(null);
    setCurrentPlatform(null);
    setWorkDetails({
      glovoHours: 0,
      glovoMinutes: 0,
      woltHours: 0,
      woltMinutes: 0,
      godisnjiHours: 0,
      godisnjiMinutes: 0,
    });
  };

  const handleDeleteWorkDetails = async () => {
    if (!currentDate) return;
    try {
      const q = query(
        collection(db, 'dailyMetrics'),
        where('userId', '==', person.id),
        where('year', '==', currentDate.year),
        where('month', '==', currentDate.month),
        where('day', '==', currentDate.day)
      );
      const snapshot = await getDocs(q);
      if (snapshot.empty) {
        setDeleteConfirmOpen(false);
        closeWorkModal();
        return;
      }
      await Promise.all(snapshot.docs.map((d) => deleteDoc(d.ref)));
      setDeleteConfirmOpen(false);
      closeWorkModal();
      refreshData();
    } catch (e) {
      console.error('Greška pri brisanju sati:', e);
      alert('Došlo je do greške prilikom brisanja sati.');
    }
  };

  const handleSaveWorkDetails = async () => {
    if (!currentDate) return;
    const metric = metricsByDate.get(
      `${currentDate.year}-${currentDate.month}-${currentDate.day}`
    );
    const existingGlovo = metric ? parseGlovoHours(metric) : 0;
    const existingWolt = metric ? parseWoltHours(metric) : 0;

    const godisnjiH = toDecimalHours(workDetails.godisnjiHours, workDetails.godisnjiMinutes);
    const glovoH = toDecimalHours(workDetails.glovoHours, workDetails.glovoMinutes);
    const woltH = toDecimalHours(workDetails.woltHours, workDetails.woltMinutes);

    let updateData;
    if (godisnjiH > 0) {
      updateData = { godisnjiHours: godisnjiH, glovoHours: 0, woltHours: 0 };
    } else {
      updateData =
        currentPlatform === 'glovo'
          ? { glovoHours: glovoH, woltHours: existingWolt, godisnjiHours: 0 }
          : { glovoHours: existingGlovo, woltHours: woltH, godisnjiHours: 0 };
    }

    try {
      const metricsCollection = collection(db, 'dailyMetrics');
      const q = query(
        metricsCollection,
        where('userId', '==', person.id),
        where('year', '==', currentDate.year),
        where('month', '==', currentDate.month),
        where('day', '==', currentDate.day)
      );
      const querySnapshot = await getDocs(q);

      const authUid = person.authId || person.authUid;
      if (authUid) updateData.authUid = authUid;
      if (!querySnapshot.empty) {
        const docRef = querySnapshot.docs[0].ref;
        await updateDoc(docRef, updateData);
      } else {
        const newMetric = {
          userId: person.id,
          year: currentDate.year,
          month: currentDate.month,
          day: currentDate.day,
          amount: 0,
          godisnjiHours: godisnjiH > 0 ? godisnjiH : 0,
          glovoHours: godisnjiH > 0 ? 0 : currentPlatform === 'glovo' ? glovoH : 0,
          woltHours: godisnjiH > 0 ? 0 : currentPlatform === 'wolt' ? woltH : 0,
        };
        if (authUid) newMetric.authUid = authUid;
        await addDoc(metricsCollection, newMetric);
      }

      closeWorkModal();
      refreshData();
    } catch (e) {
      console.error('Greška pri spremanju sati:', e);
      alert('Došlo je do greške prilikom spremanja sati.');
    }
  };

  const godisnjiActive =
    toDecimalHours(workDetails.godisnjiHours, workDetails.godisnjiMinutes) > 0;

  const DAY_NAMES = ['PON', 'UTO', 'SRI', 'ČET', 'PET', 'SUB', 'NED'];
  const HOURS_PER_WEEK = 50;

  const getHolidayName = (year, month, day) => {
    const key = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return holidaysByDate[key] || '';
  };

  const getWeekTotal = (weekIndex) => {
    let total = 0;
    const week = weeks[weekIndex];
    if (!week) return 0;
    week.dates.forEach((dateInfo) => {
      const data = getMetricForDate(dateInfo.year, dateInfo.month, dateInfo.day);
      if (data) total += data.totalHours;
    });
    return total;
  };

  const renderDayRows = () => {
    return DAY_NAMES.map((dayName, dayIndex) => (
      <tr key={dayName} className="hours-day-row">
        {dayIndex === 0 ? (
          <td rowSpan={8} className="hours-name-cell">
            <span className="hours-name-content">
              {formatNameForDisplay(person.name)}
            </span>
          </td>
        ) : null}
        {!hiddenColumns.actionsColumn && dayIndex === 0 ? (
          <td rowSpan={8} className="hours-actions-cell">
            <div className="hours-row-actions">
              {onUpdatePerson && onArchivePerson && (
                <>
                  <button
                    type="button"
                    className="hours-action-btn"
                    onClick={() => setEditPersonModalOpen(true)}
                    title="Uredi osobu"
                  >
                    <FaEdit />
                  </button>
                </>
              )}
            </div>
          </td>
        ) : null}
        <td className="hours-day-cell">{dayName}</td>
        {weeks.map((week) => {
          const dateInfo = week.dates[dayIndex];
          if (!dateInfo) {
            return (
              <React.Fragment key={week.weekIndex}>
                {!hiddenColumns.datumColumn && <td className="hours-datum-cell">–</td>}
                {!hiddenColumns.glovoSati && !hiddenColumns.woltSati ? (
                  <td colSpan={2} className="hours-glovo-cell">–</td>
                ) : (
                  <>
                    {!hiddenColumns.glovoSati && <td className="hours-glovo-cell">–</td>}
                    {!hiddenColumns.woltSati && <td className="hours-wolt-cell">–</td>}
                  </>
                )}
                {!hiddenColumns.ukupnoSati && (
                <td className={`hours-ukupno-cell ${dayName === 'NED' ? 'hours-ukupno-ned' : ''}`}>–</td>
              )}
              </React.Fragment>
            );
          }
          const data = getMetricForDate(dateInfo.year, dateInfo.month, dateInfo.day);
          const isGodisnji = data?.isGodisnji;
          const godisnjiH = data?.godisnjiHours ?? 0;
          const glovoH = data ? data.glovoHours : 0;
          const woltH = data ? data.woltHours : 0;
          const totalH = data ? data.totalHours : 0;
          const showGlovo = !hiddenColumns.glovoSati;
          const showWolt = !hiddenColumns.woltSati;
          const holidayName = getHolidayName(dateInfo.year, dateInfo.month, dateInfo.day);
          const isHoliday = Boolean(holidayName);
          const datumStr = `${dateInfo.dayName} (${dateInfo.day}.${dateInfo.month}.)`;
          return (
            <React.Fragment key={week.weekIndex}>
              {!hiddenColumns.datumColumn && (
                <td
                  className={`hours-datum-cell ${isHoliday ? 'hours-datum-holiday' : ''}`}
                  title={isHoliday ? `${datumStr} - ${holidayName}` : datumStr}
                >
                  <div className="hours-datum-main">{datumStr}</div>
                  {isHoliday ? <div className="hours-holiday-label">{holidayName}</div> : null}
                </td>
              )}
              {isGodisnji && (showGlovo || showWolt) ? (
                <td
                  colSpan={(showGlovo ? 1 : 0) + (showWolt ? 1 : 0)}
                  className="hours-godisnji-cell"
                  onClick={() => openWorkModal(dateInfo, 'glovo')}
                  title={`${dateInfo.dayName} ${datumStr} - Godišnji: ${formatDecimalHoursDisplay(godisnjiH)} (klik za uređivanje)`}
                >
                  <span className="hours-godisnji-label">Godišnji</span>
                  <span className="hours-godisnji-value">{formatDecimalHoursDisplay(godisnjiH)}</span>
                </td>
              ) : (
                <>
                  {showGlovo && (
                    <td
                      className="hours-glovo-cell"
                      onClick={() => openWorkModal(dateInfo, 'glovo')}
                      title={`${dateInfo.dayName} ${datumStr} - Glovo: ${formatDecimalHoursDisplay(glovoH)} (klik za uređivanje)`}
                    >
                      {glovoH > 0 ? formatDecimalHoursDisplay(glovoH) : '0h'}
                    </td>
                  )}
                  {showWolt && (
                    <td
                      className="hours-wolt-cell"
                      onClick={() => openWorkModal(dateInfo, 'wolt')}
                      title={`${dateInfo.dayName} ${datumStr} - Wolt: ${formatDecimalHoursDisplay(woltH)} (klik za uređivanje)`}
                    >
                      {woltH > 0 ? formatDecimalHoursDisplay(woltH) : '0h'}
                    </td>
                  )}
                </>
              )}
              {!hiddenColumns.ukupnoSati && (
                <td
                  className={`hours-ukupno-cell ${dayName === 'NED' ? 'hours-ukupno-ned' : ''} ${isHoliday ? 'hours-ukupno-holiday' : ''} ${isGodisnji ? 'hours-ukupno-godisnji' : ''}`}
                  title={`${dateInfo.dayName} ${datumStr} - Ukupno: ${formatDecimalHoursDisplay(totalH)}${isHoliday ? ` - ${holidayName}` : ''}${isGodisnji ? ' - Godišnji' : ''}`}
                >
                  <strong>{totalH > 0 ? formatDecimalHoursDisplay(totalH) : '0h'}</strong>
                </td>
              )}
            </React.Fragment>
          );
        })}
      </tr>
    ));
  };

  const renderLeftHoursRow = () => (
    <tr className="hours-left-hours-row">
      <td className="hours-day-cell hours-left-hours-dani">–</td>
      {weeks.map((week) => {
        const weekTotal = getWeekTotal(week.weekIndex);
        const leftHours = HOURS_PER_WEEK - weekTotal;
        return (
          <React.Fragment key={week.weekIndex}>
            {!hiddenColumns.datumColumn && (
              <td className="hours-datum-cell hours-left-hours-label-cell">Left hours</td>
            )}
            {!hiddenColumns.glovoSati && !hiddenColumns.woltSati ? (
              <td colSpan={2} className="hours-left-hours-cell">–</td>
            ) : (
              <>
                {!hiddenColumns.glovoSati && <td className="hours-left-hours-cell">–</td>}
                {!hiddenColumns.woltSati && <td className="hours-left-hours-cell">–</td>}
              </>
            )}
            {!hiddenColumns.ukupnoSati && (
              <td className="hours-left-hours-ukupno-cell">
                <strong>{formatDecimalHoursSigned(leftHours)}</strong>
              </td>
            )}
          </React.Fragment>
        );
      })}
    </tr>
  );

  return (
    <>
      {renderDayRows()}
      {renderLeftHoursRow()}
      {onUpdatePerson && onArchivePerson && (
        <Modal
          isOpen={editPersonModalOpen}
          onRequestClose={() => setEditPersonModalOpen(false)}
          contentLabel="Uredi osobu"
          className="modal person-edit-modal"
          overlayClassName="overlay"
        >
          <button
            className="close-modal-button"
            onClick={() => setEditPersonModalOpen(false)}
            style={{ position: 'absolute', top: 12, right: 12 }}
          >
            ×
          </button>
          <PersonEditModal
            isOpen={editPersonModalOpen}
            person={person}
            firms={firms}
            onSave={onUpdatePerson}
            onArchive={onArchivePerson}
            onClose={() => setEditPersonModalOpen(false)}
          />
        </Modal>
      )}
      <Modal
        isOpen={workModalOpen}
        onRequestClose={closeWorkModal}
        contentLabel={currentPlatform === 'glovo' ? 'Glovo sati' : 'Wolt sati'}
        className={`modal hours-cell-modal hours-cell-modal-${currentPlatform || 'glovo'}`}
        overlayClassName="overlay"
      >
        <div className="hours-cell-modal-header">
          <h2>
            {currentPlatform === 'glovo' ? 'Glovo' : 'Wolt'} sati – {currentDate?.day}.{currentDate?.month}.{currentDate?.year}.
          </h2>
          <button type="button" className="hours-cell-modal-close" onClick={closeWorkModal}>
            ×
          </button>
        </div>
        <div className="hours-cell-modal-body">
          {onExcelUpload && currentDate && (
            <section className="hours-cell-upload-section">
              <h3>Učitaj Excel za ovaj dan</h3>
              {!selectedFirm ? (
                <p className="hours-cell-upload-hint hours-cell-upload-warning">
                  Odaberite firmu u filtru prije upload-a.
                </p>
              ) : (
                <p className="hours-cell-upload-hint">
                  Spremit će se osobama firme {firms.find((f) => f.id === selectedFirm)?.name || selectedFirm}.
                </p>
              )}
              <ExcelUploader
                onDataParsed={(data, filename) => {
                  onExcelUpload(currentPlatform, currentDate, data, filename);
                  closeWorkModal();
                }}
              >
                {currentPlatform === 'glovo'
                  ? 'Učitaj Glovo Excel (Rider ID, sum CDT in minutes)'
                  : 'Učitaj Wolt Excel (Id, Total on-task time)'}
              </ExcelUploader>
            </section>
          )}
          <section className="hours-cell-edit-section">
            <h3>Ili uredi ručno za {person.name}</h3>
            <form className="edit-work-details-form">
              {currentPlatform === 'glovo' && (
                <div className="form-group duration-input-group">
                  <label>Glovo sati:</label>
                  <div className="duration-inputs">
                    <input
                      type="number"
                      min="0"
                      max="24"
                      disabled={godisnjiActive}
                      value={workDetails.glovoHours}
                      onChange={(e) =>
                        setWorkDetails({
                          ...workDetails,
                          glovoHours: Math.min(24, Math.max(0, parseInt(e.target.value) || 0)),
                        })
                      }
                    />
                    <span>h</span>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      disabled={godisnjiActive}
                      value={workDetails.glovoMinutes}
                      onChange={(e) => {
                        let m = parseInt(e.target.value) || 0;
                        let h = workDetails.glovoHours;
                        if (m >= 60) {
                          h += Math.floor(m / 60);
                          m = m % 60;
                        }
                        setWorkDetails({
                          ...workDetails,
                          glovoHours: Math.min(24, h),
                          glovoMinutes: Math.min(59, Math.max(0, m)),
                        });
                      }}
                    />
                    <span>min</span>
                  </div>
                </div>
              )}
              {currentPlatform === 'wolt' && (
                <div className="form-group duration-input-group">
                  <label>Wolt sati:</label>
                  <div className="duration-inputs">
                    <input
                      type="number"
                      min="0"
                      max="24"
                      disabled={godisnjiActive}
                      value={workDetails.woltHours}
                      onChange={(e) =>
                        setWorkDetails({
                          ...workDetails,
                          woltHours: Math.min(24, Math.max(0, parseInt(e.target.value) || 0)),
                        })
                      }
                    />
                    <span>h</span>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      disabled={godisnjiActive}
                      value={workDetails.woltMinutes}
                      onChange={(e) => {
                        let m = parseInt(e.target.value) || 0;
                        let h = workDetails.woltHours;
                        if (m >= 60) {
                          h += Math.floor(m / 60);
                          m = m % 60;
                        }
                        setWorkDetails({
                          ...workDetails,
                          woltHours: Math.min(24, h),
                          woltMinutes: Math.min(59, Math.max(0, m)),
                        });
                      }}
                    />
                    <span>min</span>
                  </div>
                </div>
              )}
              <div className="form-group duration-input-group hours-godisnji-input-group">
                <label>Godišnji (sati za ovaj dan):</label>
                <p className="hours-godisnji-hint">
                  Ako uneseš godišnji, Glovo i Wolt za ovaj dan se brišu — u tablici se prikazuje zelena
                  spojena ćelija.
                </p>
                <div className="duration-inputs">
                  <input
                    type="number"
                    min="0"
                    max="24"
                    value={workDetails.godisnjiHours}
                    onChange={(e) =>
                      setWorkDetails({
                        ...workDetails,
                        godisnjiHours: Math.min(24, Math.max(0, parseInt(e.target.value, 10) || 0)),
                      })
                    }
                  />
                  <span>h</span>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={workDetails.godisnjiMinutes}
                    onChange={(e) => {
                      let m = parseInt(e.target.value, 10) || 0;
                      let h = workDetails.godisnjiHours;
                      if (m >= 60) {
                        h += Math.floor(m / 60);
                        m = m % 60;
                      }
                      setWorkDetails({
                        ...workDetails,
                        godisnjiHours: Math.min(24, h),
                        godisnjiMinutes: Math.min(59, Math.max(0, m)),
                      });
                    }}
                  />
                  <span>min</span>
                </div>
              </div>
              <div className="button-group">
                <button type="button" className="save-button" onClick={handleSaveWorkDetails}>
                  Spremi
                </button>
                <button
                  type="button"
                  className="delete-button"
                  onClick={() => setDeleteConfirmOpen(true)}
                >
                  Obriši
                </button>
                <button type="button" className="cancel-button" onClick={closeWorkModal}>
                  Odustani
                </button>
              </div>
            </form>
          </section>
        </div>
      </Modal>
      <Modal
        isOpen={deleteConfirmOpen}
        onRequestClose={() => setDeleteConfirmOpen(false)}
        contentLabel="Potvrda brisanja sati"
        className="modal hours-delete-confirm-modal"
        overlayClassName="overlay"
      >
        <div className="hours-delete-confirm">
          <h3>Obrisati sate za ovaj dan?</h3>
          <p>
            {person.name} – {currentDate?.day}.{currentDate?.month}.{currentDate?.year}
          </p>
          <p>Ova akcija briše sve zapise za taj datum i osobu (Glovo, Wolt i godišnji).</p>
          <div className="button-group">
            <button type="button" className="delete-button" onClick={handleDeleteWorkDetails}>
              Da, obriši
            </button>
            <button
              type="button"
              className="cancel-button"
              onClick={() => setDeleteConfirmOpen(false)}
            >
              Odustani
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default React.memo(HoursRow);
